class MobileMenu {
  constructor() {
    this.mobileMenuTrigger = document.querySelector('[data-mobile-menu-trigger]');
    this.mobileMenu = document.getElementById('mobile-menu');
    this.mobileAccordions = document.querySelectorAll('[data-mobile-accordion]');
    this.nestedAccordions = document.querySelectorAll('[data-mobile-nested-accordion]');
    this.activeAccordion = null;
    this.activeNestedAccordion = null;
    this.isMenuOpen = false;

    this.init();
  }

  init() {
    if (!this.mobileMenuTrigger || !this.mobileMenu) return;

    // Set initial tab states
    this.setInitialTabStates();

    // Toggle mobile menu
    this.mobileMenuTrigger.addEventListener('click', () => this.toggleMenu());

    // Handle keyboard events
    this.mobileMenuTrigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleMenu();
      }
    });

    // Setup accordions
    this.mobileAccordions.forEach((accordion) => {
      const trigger = accordion.querySelector('[data-mobile-accordion-trigger]');
      const content = accordion.querySelector('[data-mobile-accordion-content]');

      if (!trigger || !content) return;

      trigger.addEventListener('click', () => this.toggleAccordion(accordion));

      // Keyboard navigation
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleAccordion(accordion);
        }
      });
    });

    // Setup nested accordions
    this.nestedAccordions.forEach((accordion) => {
      const trigger = accordion.querySelector('[data-mobile-nested-accordion-trigger]');
      const content = accordion.querySelector('[data-mobile-nested-accordion-content]');

      if (!trigger || !content) return;

      trigger.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent parent accordion from closing
        this.toggleNestedAccordion(accordion);
      });

      // Keyboard navigation
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          this.toggleNestedAccordion(accordion);
        }
      });
    });

    // Close menu on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isMenuOpen) {
        this.closeMenu();
      }
    });

    // Close menu on outside click
    document.addEventListener('click', (e) => {
      if (this.isMenuOpen && !this.mobileMenu.contains(e.target) && !this.mobileMenuTrigger.contains(e.target)) {
        this.closeMenu();
      }
    });
  }

  calculateMobileMenuOffset() {
    const navBottomOffset = document.getElementById('mega-menu-pxg').getBoundingClientRect().bottom;
    document.documentElement.style.setProperty('--mobile-menu-offset', `${navBottomOffset}px`);
  }

  toggleMenu() {
    this.calculateMobileMenuOffset();
    this.isMenuOpen = !this.isMenuOpen;
    this.mobileMenu.classList.toggle('-translate-x-full');
    this.mobileMenuTrigger.setAttribute('aria-expanded', this.isMenuOpen);
    this.mobileMenu.setAttribute('aria-hidden', !this.isMenuOpen);

    // Toggle menu icon
    this.mobileMenuTrigger.querySelector('.mobile-menu-icon').classList.toggle('is-active');

    // Manage focus trap and page scroll lock when menu is open
    if (this.isMenuOpen) {
      this.trapFocus();
      document.body.style.overflow = 'hidden';
    } else {
      this.closeAllAccordions();
      document.body.style.overflow = 'auto';
    }
  }

  closeMenu() {
    if (!this.isMenuOpen) return;

    this.isMenuOpen = false;
    this.mobileMenu.classList.add('-translate-x-full');
    this.mobileMenuTrigger.setAttribute('aria-expanded', 'false');
    this.mobileMenu.setAttribute('aria-hidden', 'true');
    this.mobileMenuTrigger.querySelector('.mobile-menu-icon').classList.remove('is-active');
    this.closeAllAccordions();
  }

  toggleAccordion(accordion) {
    const trigger = accordion.querySelector('[data-mobile-accordion-trigger]');
    const content = accordion.querySelector('[data-mobile-accordion-content]');
    const icon = trigger.querySelector('.mobile-accordion-icon');
    const accordionText = trigger.querySelector('.accordion-text');
    const focusableElements = content.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    // Close previously open accordion if it exists and is different
    if (this.activeAccordion && this.activeAccordion !== accordion) {
      this.closeAccordion(this.activeAccordion);
    }

    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.closeAccordion(accordion);
      this.activeAccordion = null;
      accordionText.style.color = '';
    } else {
      // Open this accordion
      trigger.setAttribute('aria-expanded', 'true');
      content.setAttribute('aria-hidden', 'false');
      content.style.maxHeight = `${content.scrollHeight}px`;
      icon.style.transform = 'rotate(180deg)';
      this.activeAccordion = accordion;

      // Make content focusable
      focusableElements.forEach((element) => {
        element.removeAttribute('tabindex');
      });
    }
  }

  closeAccordion(accordion) {
    const trigger = accordion.querySelector('[data-mobile-accordion-trigger]');
    const content = accordion.querySelector('[data-mobile-accordion-content]');
    const icon = trigger.querySelector('.mobile-accordion-icon');
    const accordionText = trigger.querySelector('.accordion-text');
    const focusableElements = content.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    trigger.setAttribute('aria-expanded', 'false');
    content.setAttribute('aria-hidden', 'true');
    content.style.maxHeight = '0';
    icon.style.transform = 'rotate(0deg)';

    // Remove Tailwind text color class
    accordionText.classList.remove('text-swing-red');

    // Make content not focusable
    focusableElements.forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
  }

  toggleNestedAccordion(accordion) {
    const trigger = accordion.querySelector('[data-mobile-nested-accordion-trigger]');
    const content = accordion.querySelector('[data-mobile-nested-accordion-content]');
    const icon = trigger.querySelector('.mobile-accordion-icon');
    const focusableElements = content.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    // Close previously open nested accordion if it exists and is different
    if (this.activeNestedAccordion && this.activeNestedAccordion !== accordion) {
      this.closeNestedAccordion(this.activeNestedAccordion);
    }

    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.closeNestedAccordion(accordion);
      this.activeNestedAccordion = null;
    } else {
      // Open this nested accordion
      trigger.setAttribute('aria-expanded', 'true');
      content.setAttribute('aria-hidden', 'false');
      content.style.maxHeight = `${content.scrollHeight}px`;
      this.activeNestedAccordion = accordion;

      // Update parent accordion's height to accommodate nested content
      const parentAccordionContent = accordion.closest('[data-mobile-accordion-content]');
      if (parentAccordionContent) {
        parentAccordionContent.style.maxHeight = `${parentAccordionContent.scrollHeight + content.scrollHeight}px`;
      }

      // Make content focusable
      focusableElements.forEach((element) => {
        element.removeAttribute('tabindex');
      });
    }
  }

  closeNestedAccordion(accordion) {
    const trigger = accordion.querySelector('[data-mobile-nested-accordion-trigger]');
    const content = accordion.querySelector('[data-mobile-nested-accordion-content]');
    const icon = trigger.querySelector('.mobile-accordion-icon');
    const focusableElements = content.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    trigger.setAttribute('aria-expanded', 'false');
    content.setAttribute('aria-hidden', 'true');
    content.style.maxHeight = '0';

    // Remove red text color
    trigger.style.color = '';

    // Make content not focusable
    focusableElements.forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
  }

  closeAllAccordions() {
    // Close main accordions
    this.mobileAccordions.forEach((accordion) => {
      this.closeAccordion(accordion);
    });

    // Close nested accordions
    this.nestedAccordions.forEach((accordion) => {
      this.closeNestedAccordion(accordion);
    });

    this.activeAccordion = null;
    this.activeNestedAccordion = null;
  }

  trapFocus() {
    const focusableElements = this.mobileMenu.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    this.mobileMenu.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    });

    firstFocusable.focus();
  }

  // Add this new method to set initial tabindex states
  setInitialTabStates() {
    // Set states for main accordions
    this.mobileAccordions.forEach((accordion) => {
      const content = accordion.querySelector('[data-mobile-accordion-content]');
      const focusableElements = content.querySelectorAll(
        'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );

      focusableElements.forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
    });

    // Set states for nested accordions
    this.nestedAccordions.forEach((accordion) => {
      const content = accordion.querySelector('[data-mobile-nested-accordion-content]');
      const focusableElements = content.querySelectorAll(
        'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );

      focusableElements.forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  /* mobile search autofocus on click - start */
  const headerSearchContainer = document.querySelector('.pxg-header-search');
  if (!headerSearchContainer) return;

  const summary = headerSearchContainer.querySelector('summary');
  const input = headerSearchContainer.querySelector('input[type="search"]');
  if (!summary || !input) return;

  summary.addEventListener('click', () => {
    headerSearchContainer.open = true;
    input.focus();
  });
  /* mobile search autofocus on click - end */
});

// Initialize mobile menu
document.addEventListener('DOMContentLoaded', () => {
  new MobileMenu();
});
